import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ROLE_TEMPLATES } from '@/types/permissions';
import { getDemoRoles } from '@/utils/demoRolePersistence';
import { wouldRoleDeletionCauseLockout, LOCKOUT_ERROR_MESSAGE } from '@/utils/lockoutProtection';
import type { RoleResponseApi } from '@/api/models/role';
import type { ModeType } from '@/types';
import { FileEdit, Plus, Lock, Copy, Trash2, Eye } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface DisplayRole {
  id: string;
  name: string;
  description: string;
  permissionCount: number;
  isLocked: boolean;
  userCount: number;
}

interface RolesPermissionsProps {
  onCustomizeRole: (roleId: string) => void;
  onCreateCustomRole: () => void;
  onCloneRole: (roleId: string) => void;
  onDeleteRole: (roleId: string) => Promise<void>;
  mode: ModeType;
  apiRoles: RoleResponseApi[];
  userRoleCounts: Record<string, number>;
}

function mapApiRolesToDisplay(apiRoles: RoleResponseApi[], userRoleCounts: Record<string, number>): DisplayRole[] {
  return apiRoles.map((role) => ({
    id: String(role.roleId),
    name: role.roleName,
    description: role.description || '',
    permissionCount: role.permissionCount,
    isLocked: role.isSystemRole,
    userCount: userRoleCounts[role.roleName] || 0,
  }));
}

function mapDemoRolesToDisplay(userRoleCounts: Record<string, number>): DisplayRole[] {
  // Use getDemoRoles() which handles session persistence
  // On first load: returns ROLE_TEMPLATES defaults
  // Within session: returns modified roles from sessionStorage
  // On refresh: resets to ROLE_TEMPLATES defaults
  const demoRoles = getDemoRoles();
  console.log('mapDemoRolesToDisplay called, got', demoRoles.length, 'roles');
  console.log('CEO role has', demoRoles.find(r => r.id === 'ceo')?.permissions.length, 'permissions');
  
  return demoRoles.map((role) => ({
    id: role.id,
    name: role.name,
    description: role.description,
    permissionCount: role.permissions.length,
    isLocked: role.isLocked || false,
    userCount: userRoleCounts[role.name] || 0,
  }));
}

export function RolesPermissions({ onCustomizeRole, onCreateCustomRole, onCloneRole, onDeleteRole, mode, apiRoles, userRoleCounts }: RolesPermissionsProps) {
  console.log('RolesPermissions component mounted/rendered');
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const roles: DisplayRole[] = mode === 'live'
    ? mapApiRolesToDisplay(apiRoles, userRoleCounts)
    : mapDemoRolesToDisplay(userRoleCounts);

  const roleIcons: Record<string, string> = {
    preparer: '📝',
    manager: '👔',
    regional: '🌍',
    cfo: '💼',
    executive: '👑',
    system_admin: '🔧',
    external: '🌐',
  };

  const handleDeleteClick = (roleId: string, roleName: string, userCount: number) => {
    // TRIGGER 3: Check for lockout protection before deleting role
    if (wouldRoleDeletionCauseLockout(roleId, mode)) {
      setDeleteError(LOCKOUT_ERROR_MESSAGE);
      setDeleteDialogOpen(true);
      return;
    }
    
    if (userCount > 0) {
      setDeleteError(`Cannot delete ${roleName}. ${userCount} user${userCount > 1 ? 's' : ''} currently assigned. Reassign users before deleting.`);
      setDeleteDialogOpen(true);
    } else {
      setRoleToDelete(roleId);
      setDeleteError(null);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (roleToDelete && !deleteError) {
      setIsDeleting(true);
      try {
        await onDeleteRole(roleToDelete);
        setDeleteDialogOpen(false);
        setRoleToDelete(null);
        setDeleteError(null);
      } catch (err) {
        setDeleteError(err instanceof Error ? err.message : 'Failed to delete role');
      } finally {
        setIsDeleting(false);
      }
      return;
    }
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
    setDeleteError(null);
  };

  const handleCloneRole = (roleId: string) => {
    onCloneRole(roleId);
  };

  const getRoleById = (roleId: string) => {
    return roles.find(r => r.id === roleId);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Roles & Permissions</h1>
        <p className="text-muted-foreground mt-2">
          These are pre-configured role bundles. Pick one and customise, or start from scratch.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {roles.map((role) => {
          // Regional Manager is disabled for Release 1 — multi-location features not active
          const isDisabledR1 = role.id === 'regional';
          return (
          <Card 
            key={role.id} 
            className={`hover:shadow-lg transition-shadow ${role.isLocked ? 'border-muted-foreground/20 bg-muted/5' : ''} ${isDisabledR1 ? 'opacity-50' : ''}`}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-4xl">{roleIcons[role.id] || '👤'}</div>
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-xl">{role.name}</CardTitle>
                      {role.isLocked && (
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex gap-2 mt-1">
                      <Badge variant="secondary">
                        {role.permissionCount} permissions enabled
                      </Badge>
                      <Badge variant="outline">
                        {role.userCount} {role.userCount === 1 ? 'user' : 'users'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
              <CardDescription className="mt-3">{role.description}</CardDescription>
            </CardHeader>
            <CardContent>
              {role.isLocked ? (
                // Locked role - View and Clone only
                <div className="space-y-2">
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => onCustomizeRole(role.id)}
                    disabled={isDisabledR1}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Permissions
                  </Button>
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => handleCloneRole(role.id)}
                    disabled={isDisabledR1}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Clone
                  </Button>
                </div>
              ) : (
                // Editable role - Edit, Clone, Delete
                <div className="space-y-2">
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => onCustomizeRole(role.id)}
                    disabled={isDisabledR1}
                  >
                    <FileEdit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => handleCloneRole(role.id)}
                      disabled={isDisabledR1}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Clone
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDeleteClick(role.id, role.name, role.userCount)}
                      disabled={isDisabledR1}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          );
        })}
      </div>

      <div className="flex justify-center pt-4">
        <Button 
          size="lg" 
          variant="outline"
          onClick={onCreateCustomRole}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Custom Role from Scratch
        </Button>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteError ? 'Cannot Delete Role' : 'Delete Role?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteError || `Delete ${getRoleById(roleToDelete || '')?.name}? This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDeleteDialogOpen(false);
              setRoleToDelete(null);
              setDeleteError(null);
            }}>
              {deleteError ? 'Close' : 'Cancel'}
            </AlertDialogCancel>
            {!deleteError && (
              <AlertDialogAction 
                onClick={handleDeleteConfirm}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
